import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { TransactionTable } from './TransactionTable';
import { IAdminTransaction } from '@/types/admin/transactions';

interface TransactionHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  transactions: IAdminTransaction[];
  residentName?: string;
}

export const TransactionHistoryModal: React.FC<TransactionHistoryModalProps> = ({
  visible,
  onClose,
  transactions,
  residentName,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Reset pagination when modal opens or transactions change
  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
    }
  }, [visible, transactions.length]);

  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Transaction History</Text>
              {residentName && (
                <Text style={styles.subtitle}>{residentName}</Text>
              )}
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity 
                style={styles.closeButton}
                onPress={onClose}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Transaction Table */}
          <View style={styles.tableContainer}>
            <TransactionTable 
              transactions={paginatedTransactions}
              maxRows={itemsPerPage}
              showPager={false}
            />
          </View>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <View style={styles.paginationContainer}>
              <Text style={styles.paginationInfo}>
                Showing {startIndex + 1} - {Math.min(endIndex, transactions.length)} of {transactions.length} transactions
              </Text>
              <View style={styles.paginationButtons}>
                <TouchableOpacity
                  style={[styles.pageButton, currentPage === 1 && styles.pageButtonDisabled]}
                  onPress={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <Text style={[styles.pageButtonText, currentPage === 1 && styles.pageButtonTextDisabled]}>
                    Previous
                  </Text>
                </TouchableOpacity>
                <Text style={styles.pageIndicator}>
                  Page {currentPage} of {totalPages}
                </Text>
                <TouchableOpacity
                  style={[styles.pageButton, currentPage === totalPages && styles.pageButtonDisabled]}
                  onPress={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  <Text style={[styles.pageButtonText, currentPage === totalPages && styles.pageButtonTextDisabled]}>
                    Next
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '95%',
    maxWidth: 1200,
    height: '90%',
    backgroundColor: ADMIN_COLORS.SURFACE,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  exportButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  exportButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    // backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 20,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
  tableContainer: {
    flex: 1,
    padding: 16,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  paginationInfo: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  paginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  pageButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  pageButtonDisabled: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  pageButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  pageButtonTextDisabled: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  pageIndicator: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
});

